import React, { useState } from 'react';
import PageLayout from '@/components/layout/pageLayout/PageLayout';
import Button from '@/components/common/button/Button';
import styles from '../../VirtualSimulationPresenter.module.css';
import type { examUIProps } from './types.props';
import AlertMessage from '@/components/layout/message/AlertMessage';
import Timer from '../timer/Timer';
import QuestionDisplay from '../questionDisplay/QuestionDisplay';
import QuestionsNavigator from '../questionNavigator/QuestionsNavigator';


const ExamUI: React.FC<examUIProps> = (props) => {
    const [isNavigatorOpen, setIsNavigatorOpen] = useState(false);
    const answeredCount = Object.keys(props.answers).length;
    const totalQuestions = props.questions.length;

    if (!props.currentQuestion) {
        return <AlertMessage type="warning" title="Sin preguntas">No hay preguntas para mostrar.</AlertMessage>;
    }

    const header = (
        <div className={styles.examHeader}>
            <Button variant="secondary" onClick={props.onGoBack}>Volver al panel</Button>
            <div className={styles.headerInfo}>
                <h2>{props.simulationName} - Sesión {props.sessionNumber}</h2>
                <Timer
                    startTime={props.sessionStartTime}
                    durationMinutes={props.durationMinutes}
                    onTimeUp={props.onTimeUp}
                />
            </div>
            <div className={styles.progressIndicator}>
                <span className={styles.progressText}>{answeredCount}/{totalQuestions}</span>
                <div className={styles.progressBar}>
                    <div
                        className={styles.progressFill}
                        style={{ width: totalQuestions > 0 ? `${(answeredCount / totalQuestions) * 100}%` : '0%' }}
                    />
                </div>
            </div>
        </div>
    );

    const main = (
        <div className={styles.examLayout}>
            <div className={styles.questionPanel}>
                <QuestionDisplay
                    question={props.currentQuestion}
                    selectedAnswer={props.answers[props.currentQuestion.id]}
                    onAnswerChange={props.onAnswerChange}
                />
                <div className={styles.navigationButtons}>
                    <Button onClick={props.onGoToPrevious} disabled={props.currentQuestionIndex === 0}>
                        ← Anterior
                    </Button>
                    <span className={styles.questionCounter}>
                        Pregunta {props.currentQuestion.display_number} de {totalQuestions}
                    </span>
                    <Button onClick={props.onGoToNext} disabled={props.currentQuestionIndex >= totalQuestions - 1}>
                        Siguiente →
                    </Button>
                </div>
            </div>

            <button
                className={`${styles.floatingNavButton} ${isNavigatorOpen ? styles.active : ''}`}
                onClick={() => setIsNavigatorOpen(!isNavigatorOpen)}
                aria-label="Abrir navegador de preguntas"
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span className={styles.navButtonBadge}>{answeredCount}/{totalQuestions}</span>
            </button>

            <aside className={`${styles.navigatorPanel} ${isNavigatorOpen ? styles.open : ''}`}>
                <div className={styles.navigatorHeader}>
                    <h3>Navegador</h3>
                    <button className={styles.closeButton} onClick={() => setIsNavigatorOpen(false)} aria-label="Cerrar">×</button>
                </div>
                <div className={styles.navigatorContent}>
                    <QuestionsNavigator
                        questions={props.questions}
                        tests={props.tests}
                        answeredQuestions={props.answers}
                        currentQuestionIndex={props.currentQuestionIndex}
                        onQuestionSelect={(index) => {
                            props.onGoToQuestion(index);
                            setIsNavigatorOpen(false);
                        }}
                    />
                    <Button
                        variant="primary"
                        fullWidth
                        className={styles.submitButton}
                        onClick={props.onSubmitSession}
                        isLoading={props.isSubmitting}
                    >
                        Finalizar sesión
                    </Button>
                </div>
            </aside>

            {isNavigatorOpen && <div className={styles.overlay} onClick={() => setIsNavigatorOpen(false)} />}
        </div>
    );

    return <PageLayout header={header} main={main} />;
};

export default ExamUI;
